import type { RelatedScheduleResponse, ScoreboardResponse } from '@on3/api';
import { getUserTimeZone } from '@on3/ui-lib/utils/date';
import { isBefore, startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { ScheduleItem } from '../ScheduleItem/ScheduleItem';
import styles from './CollegeSchedule.module.scss';

interface IScheduleModuleProps {
  full?: boolean;
  schedule?: ScoreboardResponse[];
  relatedModel?: RelatedScheduleResponse;
}

const VISIBLE_GAMES = 5;

export const CollegeSchedule = ({
  full,
  schedule,
  relatedModel,
}: IScheduleModuleProps) => {
  const userTimeZone = getUserTimeZone();

  const list = useMemo(() => {
    // If full, return the entire schedule
    if (full) return schedule;

    // Otherwise, return a mix of past and future games
    const today = startOfDay(new Date());
    const pastGames: ScoreboardResponse[] = [];
    const futureGames: ScoreboardResponse[] = [];

    schedule?.forEach((game) => {
      const gameDate = new Date((game?.startDateUtc || 0) * 1000);

      if (isBefore(gameDate, today)) {
        pastGames.push(game);
      } else {
        futureGames.push(game);
      }
    });

    if (pastGames.length < 5) {
      const sliceNumber = VISIBLE_GAMES - pastGames.length;

      return [...pastGames, ...futureGames.slice(0, sliceNumber)];
    } else if (futureGames.length < 5) {
      const sliceNumber = VISIBLE_GAMES - futureGames.length;

      return [...pastGames.slice(-sliceNumber), ...futureGames];
    }

    return [...pastGames.slice(-5), ...futureGames.slice(0, 5)];
  }, [full, schedule]);

  return (
    <section className={styles.block}>
      <div className={styles.schedule}>
        <table>
          <thead>
            <tr>
              <th id="date">Date</th>
              <th id="opponent">Opponent</th>
              <th id="score">Score</th>
              <th id="prediction">Prediction</th>
              <th id="winProbability">Win Probability</th>
            </tr>
          </thead>
          <tbody>
            {list?.map((game, i) => (
              <ScheduleItem
                game={game}
                key={game.key}
                relatedModel={relatedModel}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.disclaimer}>All times are in {userTimeZone} Time</p>
    </section>
  );
};
